import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';

import {
  ManagerProgressionTaskSlug,
  ManagerProgressionTaskState,
} from '@sorare/core/src/__generated__/globalTypes';
import { isType } from '@sorare/core/src/lib/gql';

import { CardCollectionTask } from '../CardCollectionTask';
import { ClaimedTask } from '../ClaimedTask';
import { CollectionCompletionTask } from '../CollectionCompletionTask';
import { DailyActionTask } from '../DailyActionTask';
import { DailyTask } from '../DailyTask';
import { DecisivePlayerPickerTaskStatus } from '../DailyTask/DecisivePlayerPickerTaskStatus';
import { DropChallengeButton } from '../DailyTask/DropChallengeButton';
import { GameWeeklyTask } from '../GameWeeklyTask';
import { OnboardingTask } from '../OnboardingTask';
import { StreakTask } from '../StreakTask';
import { ThresholdsDuoTask } from '../ThresholdsDuoTask';
import { UnclaimedTask } from '../UnclaimedTask';
import { getTaskColors } from '../getTaskColors';
import type { AnyTask_taskInterface } from './__generated__/index.graphql';

type Props = {
  task: AnyTask_taskInterface;
  featured?: boolean;
  onDropChallengeClick?: () => void;
  onDecisivePlayerPickerClick?: Nullable<() => void>;
  onClaim?: () => void;
  loading?: boolean;
  disableDailyActionTasks?: boolean;
};
export const AnyTask = ({
  task,
  featured,
  onDropChallengeClick,
  onDecisivePlayerPickerClick,
  onClaim,
  loading,
  disableDailyActionTasks,
}: Props) => {
  const { borderColor } = getTaskColors(task, featured) || {};

  if (
    task.aasmState === ManagerProgressionTaskState.CLAIMED &&
    task.name !== ManagerProgressionTaskSlug.DAILY_ACTION
  ) {
    return <ClaimedTask claimedTask={task} />;
  }
  if (
    task.aasmState === ManagerProgressionTaskState.COMPLETED &&
    task.name !== ManagerProgressionTaskSlug.DAILY_ACTION
  ) {
    return (
      <UnclaimedTask
        unclaimedTask={task}
        borderColor={borderColor}
        featured={featured}
        onClaim={onClaim}
        loading={loading}
      />
    );
  }
  if (isType(task, 'CardsCountTask')) {
    return <CardCollectionTask cardCollectionTask={task} featured={featured} />;
  }
  if (isType(task, 'CompleteCollectionsCountTask')) {
    return (
      <CollectionCompletionTask
        collectionCompletionTask={task}
        featured={featured}
      />
    );
  }
  if (isType(task, 'DropChallengeTask')) {
    return (
      <DailyTask
        dailyTask={task}
        featured={featured}
        borderColor={borderColor}
        action={
          <DropChallengeButton onClick={onDropChallengeClick} task={task} />
        }
      />
    );
  }
  if (isType(task, 'DecisivePlayerPickerTask')) {
    return (
      <DailyTask
        dailyTask={task}
        featured={featured}
        borderColor={borderColor}
        action={<DecisivePlayerPickerTaskStatus task={task} />}
      />
    );
  }
  if (isType(task, 'GameWeeklyTask')) {
    return (
      <GameWeeklyTask
        gameWeeklyTask={task}
        featured={featured}
        borderColor={borderColor}
      />
    );
  }
  if (isType(task, 'ThresholdsStreakTask')) {
    return <StreakTask streakTask={task} featured={featured} />;
  }
  if (isType(task, 'ThresholdsDuoConnectionTask')) {
    return <ThresholdsDuoTask task={task} />;
  }
  if (isType(task, 'Task')) {
    return <OnboardingTask onboardingTask={task} borderColor={borderColor} />;
  }
  if (isType(task, 'DailyActionTask') && !disableDailyActionTasks) {
    return (
      <DailyActionTask
        task={task}
        onDropChallengeClick={onDropChallengeClick}
        onDecisivePlayerPickerClick={onDecisivePlayerPickerClick}
      />
    );
  }
  // for unknown tasks, we don't want to show anything
  return null;
};

AnyTask.fragments = {
  taskInterface: gql`
    fragment AnyTask_taskInterface on TaskInterface {
      id
      aasmState
      rewardConfigs {
        id
      }
      ...UnclaimedTask_taskInterface
      ...CardCollectionTask_taskInterface
      ...CollectionCompletionTask_taskInterface
      ...DailyTask_taskInterface
      ...GameWeeklyTask_taskInterface
      ...StreakTask_taskInterface
      ...ThresholdsDuoTask_taskInterface
      ...OnboardingTask_taskInterface
      ...getTaskColors_taskInterface
      ...DecisivePlayerPickerTaskStatus_decisivePlayerPickerTask
      ...DailyActionTask_taskInterface @skip(if: $disableDailyActionTasks)
    }
    ${UnclaimedTask.fragments.taskInterface}
    ${CardCollectionTask.fragments.taskInterface}
    ${CollectionCompletionTask.fragments.taskInterface}
    ${DailyTask.fragments.taskInterface}
    ${GameWeeklyTask.fragments.taskInterface}
    ${StreakTask.fragments.taskInterface}
    ${ThresholdsDuoTask.fragments.taskInterface}
    ${OnboardingTask.fragments.taskInterface}
    ${getTaskColors.fragments.taskInterface}
    ${DecisivePlayerPickerTaskStatus.fragments.decisivePlayerPickerTask}
    ${DailyActionTask.fragments.taskInterface}
  ` as TypedDocumentNode<AnyTask_taskInterface>,
};
